import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { imageurl } from "../../App";
import { loadStoreSettings } from "../../util/storeSettings";
import { resolveFooterMenus } from "../../themes/footerMenu";
import { readCachedStoreSettings } from "../../util/storeLogoHelper";
import { brandLogoForSurface } from "../../assets/img/logo/brandLogos";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
} from "react-icons/fa";

function FooterLink({ to, children }) {
  const href = String(to || "").trim();
  if (!href) return <span>{children}</span>;
  if (/^(https?:|mailto:|tel:)/i.test(href)) {
    return (
      <a href={href} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }
  return <Link to={href.startsWith("/") ? href : `/${href}`}>{children}</Link>;
}

export default function Footer({ themeSettings = null, embedded = false, storeSettingsOverride = null }) {
  const [storeSettings, setStoreSettings] = useState(() => {
    if (storeSettingsOverride) return storeSettingsOverride;
    const saved = readCachedStoreSettings();
    return saved && (saved.storeName || saved.logoUrl) ? saved : null;
  });
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const theme = themeSettings || {};

  useEffect(() => {
    if (storeSettingsOverride) {
      setStoreSettings(storeSettingsOverride);
      return undefined;
    }
    let mounted = true;
    loadStoreSettings()
      .then((settings) => {
        if (mounted && settings && (settings.storeName || settings.logoUrl)) {
          setStoreSettings(settings);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [storeSettingsOverride]);

  const storeName = storeSettings?.storeName || "Gootocart";
  const logoUrl = storeSettings?.logoUrl;
  const showLogo = theme.showFooterLogo !== false;
  const showSocial = theme.showFooterSocial !== false;
  const showContact = theme.showFooterContact !== false;
  const showNewsletter = theme.showNewsletterInFooter === true;
  const menus = resolveFooterMenus(theme);
  const description =
    theme.footerTagline ||
    storeSettings?.footerDescription ||
    "Pakistan's premium online destination for authentic electronics, fashion, appliances, and lifestyle essentials.";
  const copyright =
    theme.footerCopyright || `© ${new Date().getFullYear()} ${storeName}. All rights reserved.`;

  const socials = [
    { href: storeSettings?.facebookUrl, label: "Facebook", icon: <FaFacebookF size={14} /> },
    { href: storeSettings?.twitterUrl, label: "Twitter", icon: <FaTwitter size={14} /> },
    { href: storeSettings?.instagramUrl, label: "Instagram", icon: <FaInstagram size={14} /> },
    { href: storeSettings?.linkedinUrl, label: "LinkedIn", icon: <FaLinkedinIn size={14} /> },
    {
      href: storeSettings?.whatsappNumber
        ? storeSettings.whatsappNumber.startsWith("http")
          ? storeSettings.whatsappNumber
          : `https://wa.me/${String(storeSettings.whatsappNumber).replace(/[^0-9]/g, "")}`
        : "",
      label: "WhatsApp",
      icon: <FaWhatsapp size={15} />,
    },
  ].filter((item) => item.href);

  return (
    <footer
      className={`footer-modern ${embedded ? "is-preview" : ""}`}
      style={{
        background: theme.footerBackground || "#0f172a",
        "--footer-heading": theme.footerHeadingColor || "#f8fafc",
        "--footer-text": theme.footerTextColor || "#94a3b8",
        "--footer-line": "rgba(255,255,255,0.12)",
      }}
    >
      <div className="container">
        <div className="footer-grid">
          {(showLogo || description || (showSocial && socials.length > 0)) && (
            <div className="footer-brand footer-brand-col">
              {showLogo && (
                <div className="d-flex align-items-center gap-2 mb-3">
                  <img
                    src={
                      logoUrl
                        ? logoUrl.startsWith("http")
                          ? logoUrl
                          : `${imageurl}/${logoUrl}`
                        : brandLogoForSurface(theme.footerBackground || "#0f172a")
                    }
                    alt={storeName}
                    style={{ height: "72px", width: "auto", maxWidth: "160px", objectFit: "contain" }}
                  />
                </div>
              )}
              {description && <p>{description}</p>}
              {showSocial && socials.length > 0 && (
                <div className="d-flex gap-2 mt-3">
                  {socials.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className="nav-action-btn"
                      style={{ background: "rgba(255,255,255,0.08)", color: "var(--footer-heading)", width: "36px", height: "36px" }}
                      aria-label={item.label}
                    >
                      {item.icon}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {menus.map((column) => (
            <div className="footer-col" key={column.id}>
              {column.title && <h6>{column.title}</h6>}
              <ul className="footer-links">
                {column.items.map((item) => (
                  <li key={item.id}>
                    <FooterLink to={item.link}>{item.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {showContact && (
            <div className="footer-col footer-contact footer-contact-col">
              <h6>Get in Touch</h6>
              <ul className="footer-links">
                <li className="d-flex align-items-start gap-2 mb-2">
                  <FaMapMarkerAlt className="mt-1 flex-shrink-0" style={{ color: "var(--footer-heading)" }} />
                  <span>
                    {storeSettings?.warehouseAddress || "Shop G21, KN Gohar Society, Malir Courts, Karachi"}
                  </span>
                </li>
                <li className="d-flex align-items-center gap-2 mb-2">
                  <FaPhoneAlt className="flex-shrink-0" style={{ color: "var(--footer-heading)" }} />
                  <span>{storeSettings?.supportPhone || "0332-2417001"}</span>
                </li>
                {storeSettings?.supportEmail ? (
                  <li className="d-flex align-items-center gap-2 mb-2">
                    <FaEnvelope className="flex-shrink-0" style={{ color: "var(--footer-heading)" }} />
                    <span>{storeSettings.supportEmail}</span>
                  </li>
                ) : null}
              </ul>
            </div>
          )}

          {showNewsletter && (
            <div className="footer-col footer-newsletter-col">
              <h6>{theme.footerNewsletterTitle || "Newsletter"}</h6>
              <p>New arrivals and private offers.</p>
              {joined ? (
                <p>You are on the list.</p>
              ) : (
                <form
                  className="footer-newsletter"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (email.trim()) setJoined(true);
                  }}
                >
                  <input
                    type="email"
                    required
                    placeholder="Email address"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    aria-label="Email address"
                  />
                  <button
                    type="submit"
                    style={{
                      background: theme.buttonColor || theme.footerHeadingColor || "#ffffff",
                      color: theme.buttonTextColor || "#111827",
                    }}
                  >
                    Join
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        <div className="footer-bottom">
          <div>{copyright}</div>
        </div>
      </div>
    </footer>
  );
}
